/**
 * Fanatic portal shell (AC-FANATIC-004).
 *
 * A Fanatic account lives in the shared `public-fanatics` realm, so its access
 * token carries **no** `tid` claim. Team context therefore comes from this
 * shell: the switcher writes the selection into `SessionContext.activeTenant`,
 * which is what puts `X-Tenant-ID` on every downstream read, and mirrors it
 * into `?team={slug}` so a refresh or a shared link lands on the same team.
 *
 * The shell owns the grant list ("teams I follow") and hands it to every child
 * view through {@link useFanaticPortal}.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useSearchParams } from 'react-router-dom';

import { useActiveEvent } from '../../app/active-event-context';
import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { ApiError } from '../../lib/api-error';
import type { FanaticGrant, FanaticGrantCounts, TeamEvent } from '../../lib/api-types';
import { FANATIC_TEAM_PARAM, FANATIC_VIEWS, ROUTES } from '../../lib/constants';
import { EVENT_STATUS_LABELS, formatEventDateTime } from '../../lib/event-format';

import { TeamSwitcher } from './team-switcher';

/** Followed teams a switcher can realistically hold without pagination. */
const GRANT_PAGE_SIZE = 100;

/**
 * Every fanatic screen shows failures as prose, never a stack trace. Services
 * answer with RFC 7807 whose `title` is already reader-facing, so prefer it and
 * fall back to a screen-specific sentence for transport-level surprises.
 */
export function toFanaticMessage(cause: unknown, fallback: string): string {
  if (ApiError.isApiError(cause)) {
    return cause.isOffline || cause.message === '' ? fallback : cause.message;
  }
  return fallback;
}

export interface FanaticPortalContextValue {
  /** Every grant, including `pending_invite` and `revoked` history. */
  grants: FanaticGrant[];
  /** Only the grants that actually unlock data today. */
  activeGrants: FanaticGrant[];
  counts: FanaticGrantCounts | null;
  activeGrant: FanaticGrant | null;
  /**
   * The tenant id the API client is *actually* sending as `X-Tenant-ID`.
   *
   * Child views must gate their fetches on this rather than on
   * `activeGrant.tenant_id`: React runs child effects before parent effects, so
   * a child keyed on the grant would fire its first request in the commit
   * *before* this shell has published the selection to the session — and that
   * request would go out with no tenant header at all.
   */
  tenantId: string | null;
  isLoading: boolean;
  error: string | null;
  selectTeam: (grant: FanaticGrant) => void;
  /** Re-read the grant list, e.g. after a follow or an unfollow. */
  reload: () => void;
  /** Append `?team=` to an in-portal link so the context survives navigation. */
  withTeam: (view: string) => string;
}

const FanaticPortalContext = createContext<FanaticPortalContextValue | null>(null);

class FanaticPortalContextError extends Error {
  constructor() {
    super('useFanaticPortal() must be called inside <FanaticPortalPage>.');
    this.name = 'FanaticPortalContextError';
  }
}

export function useFanaticPortal(): FanaticPortalContextValue {
  const value = useContext(FanaticPortalContext);
  if (value === null) throw new FanaticPortalContextError();
  return value;
}

function portalPath(view: string, slug: string | null): string {
  const base = view === '' ? ROUTES.fanaticPortal : `${ROUTES.fanaticPortal}/${view}`;
  return slug === null ? base : `${base}?${FANATIC_TEAM_PARAM}=${encodeURIComponent(slug)}`;
}

const NAV_ITEMS: ReadonlyArray<{ view: string; label: string }> = [
  { view: FANATIC_VIEWS.home, label: 'My teams' },
  { view: FANATIC_VIEWS.gameSummary, label: 'Game summary' },
  { view: FANATIC_VIEWS.liveVideo, label: 'Live video' },
  { view: FANATIC_VIEWS.browse, label: 'Find teams' },
  { view: FANATIC_VIEWS.account, label: 'Account' },
];

export function FanaticPortalPage(): JSX.Element {
  const apiClient = useApiClient();
  const { activeTenant, setActiveTenant } = useSession();
  const { setActiveEvent } = useActiveEvent();
  const [searchParams, setSearchParams] = useSearchParams();
  const teamParam = searchParams.get(FANATIC_TEAM_PARAM);

  const [grants, setGrants] = useState<FanaticGrant[]>([]);
  const [counts, setCounts] = useState<FanaticGrantCounts | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    void (async () => {
      try {
        const page = await apiClient.listFanaticGrants(
          { status: 'all', sort: 'team_name', direction: 'asc', page_size: GRANT_PAGE_SIZE },
          controller.signal
        );
        setGrants(page.grants);
        setCounts(page.counts);
        setError(null);
      } catch (cause) {
        if (controller.signal.aborted) return;
        setError(toFanaticMessage(cause, 'We could not load the teams you follow.'));
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    })();
    return () => controller.abort();
  }, [apiClient, reloadToken]);

  const activeGrants = useMemo(() => grants.filter((grant) => grant.status === 'active'), [grants]);

  const activeGrant = useMemo(() => {
    const bySlug = activeGrants.find((grant) => grant.team_slug === teamParam);
    if (bySlug !== undefined) return bySlug;
    const byStored = activeGrants.find((grant) => grant.tenant_id === activeTenant?.tenantId);
    return byStored ?? activeGrants[0] ?? null;
  }, [activeGrants, activeTenant?.tenantId, teamParam]);

  // Keep the API client's tenant header and the URL in step with the choice.
  useEffect(() => {
    if (activeGrant === null) {
      if (activeTenant !== null) setActiveTenant(null);
      return;
    }
    if (activeTenant?.tenantId !== activeGrant.tenant_id) {
      setActiveTenant({
        tenantId: activeGrant.tenant_id,
        slug: activeGrant.team_slug,
        name: activeGrant.team_name,
      });
    }
    if (activeGrant.team_slug !== null && teamParam !== activeGrant.team_slug) {
      const next = new URLSearchParams(searchParams);
      next.set(FANATIC_TEAM_PARAM, activeGrant.team_slug);
      setSearchParams(next, { replace: true });
    }
  }, [activeGrant, activeTenant, searchParams, setActiveTenant, setSearchParams, teamParam]);

  const selectTeam = useCallback(
    (grant: FanaticGrant) => {
      if (grant.tenant_id === activeTenant?.tenantId) return;
      // The previously selected game belongs to the previous team: drop it so a
      // switch never renders one team's stats under another team's name.
      setActiveEvent(null);
      setActiveTenant({
        tenantId: grant.tenant_id,
        slug: grant.team_slug,
        name: grant.team_name,
      });
      const next = new URLSearchParams(searchParams);
      if (grant.team_slug === null) next.delete(FANATIC_TEAM_PARAM);
      else next.set(FANATIC_TEAM_PARAM, grant.team_slug);
      setSearchParams(next);
    },
    [activeTenant?.tenantId, searchParams, setActiveEvent, setActiveTenant, setSearchParams]
  );

  const withTeam = useCallback(
    (view: string) => portalPath(view, activeGrant?.team_slug ?? null),
    [activeGrant?.team_slug]
  );

  const value = useMemo<FanaticPortalContextValue>(
    () => ({
      grants,
      activeGrants,
      counts,
      activeGrant,
      tenantId: activeTenant?.tenantId ?? null,
      isLoading,
      error,
      selectTeam,
      reload: () => setReloadToken((token) => token + 1),
      withTeam,
    }),
    [
      activeGrant,
      activeGrants,
      activeTenant?.tenantId,
      counts,
      error,
      grants,
      isLoading,
      selectTeam,
      withTeam,
    ]
  );

  return (
    <FanaticPortalContext.Provider value={value}>
      <section className="fanatic" aria-label="Fanatic portal">
        {/* One top-level heading for the whole portal; child views use h2. */}
        <h1 className="visually-hidden">Fanatic portal</h1>
        <div className="fanatic__bar">
          <TeamSwitcher
            grants={grants}
            activeGrant={activeGrant}
            isLoading={isLoading}
            onSelect={selectTeam}
          />
          <nav className="fanatic__nav" aria-label="Fanatic portal views">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.label}
                className="fanatic__nav-link"
                end={item.view === FANATIC_VIEWS.home}
                to={withTeam(item.view)}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>

        {error !== null ? (
          <p className="banner banner--error" role="alert">
            {error}{' '}
            <button className="btn btn--ghost btn--sm" onClick={value.reload} type="button">
              Try again
            </button>
          </p>
        ) : null}

        <div className="fanatic__body">
          <Outlet />
        </div>
      </section>
    </FanaticPortalContext.Provider>
  );
}

/**
 * Portal home: the games of the selected team. Choosing one stores it as the
 * session's active event so Game Summary and Live Video open on it.
 */
export function FanaticHomePage(): JSX.Element {
  const apiClient = useApiClient();
  const {
    activeGrant,
    activeGrants,
    counts,
    isLoading: isGrantsLoading,
    tenantId,
    withTeam,
  } = useFanaticPortal();
  const { activeEventId, setActiveEvent } = useActiveEvent();
  const [events, setEvents] = useState<TeamEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (tenantId === null) {
      setEvents([]);
      return;
    }
    const controller = new AbortController();
    setIsLoading(true);
    void (async () => {
      try {
        const page = await apiClient.listEvents(
          { limit: 25, sort_by: 'scheduled_at', sort_order: 'desc' },
          controller.signal
        );
        setEvents(page.items);
        setError(null);
      } catch (cause) {
        if (controller.signal.aborted) return;
        setError(toFanaticMessage(cause, 'We could not load this team’s games.'));
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    })();
    return () => controller.abort();
  }, [apiClient, tenantId]);

  if (isGrantsLoading) {
    return <p className="fan-empty">Loading the teams you follow…</p>;
  }

  if (activeGrants.length === 0) {
    return (
      <section className="fan-panel">
        <div className="fan-panel__head">
          <h2 className="fan-panel__title">You are not following any teams yet</h2>
        </div>
        <p className="fan-panel__hint">
          Find a team to follow and its scores, team stats and — where the team’s video provider
          allows it — its live stream show up here.
          {counts !== null && counts.pending_invite > 0
            ? ` You also have ${counts.pending_invite} pending invite${
                counts.pending_invite === 1 ? '' : 's'
              } waiting in your email.`
            : ''}
        </p>
        <div className="fan-panel__actions">
          <Link className="btn btn--primary" to={withTeam(FANATIC_VIEWS.browse)}>
            Find teams to follow
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="fan-panel">
      <div className="fan-panel__head">
        <h2 className="fan-panel__title">{activeGrant?.team_name ?? 'Selected team'} — games</h2>
        <p className="fan-panel__hint">
          Spectator view: team totals only. Individual player stats stay with the coaching staff.
        </p>
      </div>

      {error !== null ? (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      ) : null}

      {isLoading ? <p className="fan-empty">Loading games…</p> : null}

      {!isLoading && error === null && events.length === 0 ? (
        <p className="fan-empty">This team has not posted any games yet.</p>
      ) : null}

      {events.length > 0 ? (
        <ul className="fan-list">
          {events.map((event) => (
            <li className="fan-list__row" key={event.event_id}>
              <div className="fan-list__main">
                <span className="fan-list__name">{event.name}</span>
                <span className="fan-list__meta">
                  {formatEventDateTime(event.scheduled_at)} · {EVENT_STATUS_LABELS[event.status]}
                  {event.home_score !== null && event.away_score !== null
                    ? ` · ${event.home_score}–${event.away_score}`
                    : ''}
                </span>
              </div>
              <div className="fan-list__actions">
                {activeEventId === event.event_id ? (
                  <span className="fan-badge fan-badge--active">Selected</span>
                ) : (
                  <button
                    className="btn btn--secondary btn--sm"
                    onClick={() => setActiveEvent(event)}
                    type="button"
                  >
                    Select
                  </button>
                )}
                <Link
                  className="btn btn--ghost btn--sm"
                  onClick={() => setActiveEvent(event)}
                  to={withTeam(FANATIC_VIEWS.gameSummary)}
                >
                  Game summary
                </Link>
                <Link
                  className="btn btn--ghost btn--sm"
                  onClick={() => setActiveEvent(event)}
                  to={withTeam(FANATIC_VIEWS.liveVideo)}
                >
                  Live video
                </Link>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
